import { Button } from '@liza/ui/components/ui/button';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A field that idles as a "+ Label" affordance and expands inline when tapped
 * (or whenever it already carries a value — e.g. reopening edit mode, or a
 * remount after a mode switch). The expanded row carries an X that clears the
 * value and collapses — a collapsed row always means "not set", so closing
 * and clearing are the same gesture.
 */
export function CollapsedFieldRow({
  name,
  label,
  hasValue,
  onClear,
  children,
}: {
  /** Stable, locale-independent key for the row's test ids. */
  name: string;
  label: string;
  hasValue: boolean;
  onClear: () => void;
  children: React.ReactNode;
}): React.ReactNode {
  const { t } = useTranslation('common');
  const [opened, setOpened] = useState(false);
  if (!opened && !hasValue) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="justify-start px-1 text-muted-foreground"
        onClick={() => setOpened(true)}
        aria-expanded={false}
        data-testid={`add-${name}`}
      >
        <Plus className="size-4" aria-hidden="true" />
        {label}
      </Button>
    );
  }
  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">{children}</div>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="mt-6 shrink-0 text-muted-foreground"
        aria-label={t('clearField', { label })}
        data-testid={`clear-${name}`}
        onClick={() => {
          onClear();
          setOpened(false);
        }}
      >
        <X className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
